// Usuarios (autenticacion.usuarios). Sistemas → Gestión de Autenticación → Usuarios.
// Alta vía RPC register (hashea la contraseña); reset de contraseña vía
// admin_set_password. Edición de datos por REST. (La contraseña NUNCA se escribe
// en texto plano.)
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Loader2, Pencil, Plus, ShieldCheck, ShieldOff, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { useCurrentUser } from '@/lib/hooks/useAuth';
import { usersAPI } from '@/lib/api/users.api';
import { Campo, Modal, Switch, auth, fieldCls } from './shared';

interface Usuario { id: string; username: string; email: string; activo: boolean; empresa_id: string; perfil_id: string; empresas?: { nombre: string } | null; perfiles?: { nombre: string } | null }
interface Opt { id: string; nombre: string }
const vacio = { username: '', email: '', password: '', empresa_id: '', perfil_id: '', activo: true, centros: [] as string[] };

export function Usuarios() {
  const { t } = useTranslation('admin');
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: yo } = useCurrentUser();
  const { data: rows = [], isFetching } = useQuery<Usuario[]>({
    queryKey: ['adm-usuarios'],
    queryFn: () => auth.list<Usuario>('usuarios', '?select=id,username,email,activo,empresa_id,perfil_id,empresas(nombre),perfiles(nombre)&order=username'),
  });
  // MFA (TrackConstruccion, script 006): solo si la BD lo tiene y el usuario es nivel 5.
  const { data: mfa } = useQuery<Map<string, { activo: boolean; requerido: boolean }>>({
    queryKey: ['adm-mfa-usuarios'],
    queryFn: async () => {
      const r = (await apiClient.post('/rpc/mfa_usuarios', {})).data as Array<{ usuario_id: string; activo: boolean; requerido: boolean }>;
      return new Map(r.map((x) => [x.usuario_id, x]));
    },
    retry: false,
  });
  const restablecerMfa = useMutation({
    mutationFn: (id: string) => apiClient.post('/rpc/mfa_restablecer', { _usuario_id: id }),
    onSuccess: () => { toast.success(t('usuarios_mfa_restablecido')); void qc.invalidateQueries({ queryKey: ['adm-mfa-usuarios'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const { data: empresas = [] } = useQuery<Opt[]>({ queryKey: ['adm-empresas-opt'], queryFn: () => auth.list<Opt>('empresas', '?select=id,nombre&order=nombre') });
  const { data: perfiles = [] } = useQuery<Opt[]>({ queryKey: ['adm-perfiles-opt'], queryFn: () => auth.list<Opt>('perfiles', '?select=id,nombre&order=nombre') });
  // Centros de costo de la empresa actual (partner con tipo 'centro_costo')
  const { data: centrosCosto = [] } = useQuery<Array<{ id: string; razon_social: string; tipo_partner: string[] }>>({
    queryKey: ['adm-centros-costo'],
    queryFn: async () => {
      const { data } = await apiClient.post('/rpc/sri_partners', {}, { headers: { 'Accept-Profile': 'financiero', 'Content-Profile': 'financiero' } });
      return ((data as Array<{ id: string; razon_social: string; tipo_partner: string[] }>) || []).filter((p) => (p.tipo_partner || []).includes('centro_costo'));
    },
  });

  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<typeof vacio>(vacio);
  const [abierto, setAbierto] = useState(false);

  const abrir = (u?: Usuario) => {
    if (u) {
      setForm({ username: u.username, email: u.email, password: '', empresa_id: u.empresa_id, perfil_id: u.perfil_id, activo: u.activo, centros: [] });
      usersAPI.getCentrosCosto(u.id).then((cc) => setForm((f) => ({ ...f, centros: cc.map((c) => c.id) }))).catch(() => {});
    } else setForm(vacio);
    setEditId(u?.id ?? null); setAbierto(true);
  };

  const toggleCentro = (id: string) =>
    setForm((f) => ({ ...f, centros: f.centros.includes(id) ? f.centros.filter((x) => x !== id) : [...f.centros, id] }));

  const guardar = useMutation({
    mutationFn: async () => {
      let uid = editId;
      if (editId) {
        // No se reasigna la empresa al editar (es la frontera de tenant; cambiarla viola la RLS de usuarios).
        await auth.update('usuarios', editId, { username: form.username, email: form.email, perfil_id: form.perfil_id, activo: form.activo, updated_at: new Date().toISOString() });
        if (form.password.trim()) await apiClient.post('/rpc/admin_set_password', { _user_id: editId, _password: form.password });
      } else {
        await apiClient.post('/rpc/register', { _username: form.username, _email: form.email, _password: form.password, _empresa_id: form.empresa_id, _perfil_id: form.perfil_id });
        const nuevos = await auth.list<{ id: string }>('usuarios', `?select=id&username=eq.${encodeURIComponent(form.username)}&limit=1`);
        uid = nuevos[0]?.id ?? null;
      }
      // Centros de costo (no debe tumbar el alta si falla, p.ej. usuario de otra empresa).
      if (uid) {
        try { await usersAPI.setCentrosCosto(uid, form.centros); }
        catch { toast.warning(t('usuarios_centros_warn')); }
      }
    },
    onSuccess: () => { toast.success(t('usuarios_guardado')); setAbierto(false); void qc.invalidateQueries({ queryKey: ['adm-usuarios'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const eliminar = useMutation({
    mutationFn: (id: string) => auth.remove('usuarios', id),
    onSuccess: () => { toast.success(t('usuarios_eliminado')); void qc.invalidateQueries({ queryKey: ['adm-usuarios'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('usuarios_titulo')}</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> {t('usuarios_nuevo')}</Button>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">{t('usuarios_card', { n: rows.length })}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">{t('comun_usuario')}</th><th className="py-2 pr-3">{t('comun_email')}</th><th className="py-2 pr-3">{t('comun_empresa')}</th><th className="py-2 pr-3">{t('comun_perfil')}</th><th className="py-2 pr-3">{t('comun_estado')}</th>{mfa && <th className="py-2 pr-3">{t('usuarios_mfa')}</th>}<th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={mfa ? 7 : 6} className="py-8 text-center text-muted-foreground">{t('usuarios_vacio')}</td></tr>}
              {rows.map((u) => (
                <tr key={u.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-medium">{u.username}</td>
                  <td className="py-2 pr-3 text-muted-foreground">{u.email}</td>
                  <td className="py-2 pr-3">{u.empresas?.nombre ?? '—'}</td>
                  <td className="py-2 pr-3 capitalize">{u.perfiles?.nombre ?? '—'}</td>
                  <td className="py-2 pr-3">{u.activo ? t('comun_activo') : t('comun_inactivo')}</td>
                  {mfa && (
                    <td className="py-2 pr-3 text-xs">
                      {mfa.get(u.id)?.activo
                        ? <span className="inline-flex items-center gap-1 text-emerald-600"><ShieldCheck className="h-3.5 w-3.5" />{t('usuarios_mfa_activo')}</span>
                        : <span className={mfa.get(u.id)?.requerido ? 'text-amber-600' : 'text-muted-foreground'}>{mfa.get(u.id)?.requerido ? t('usuarios_mfa_pendiente') : t('usuarios_mfa_no')}</span>}
                    </td>
                  )}
                  <td className="py-2 text-right whitespace-nowrap">
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted" title={t('comun_editar')} onClick={() => abrir(u)}><Pencil className="w-4 h-4" /></button>
                    {mfa?.get(u.id)?.activo && u.id !== yo?.user?.id && (
                      <button className="inline-flex w-7 h-7 items-center justify-center rounded-md hover:bg-muted ml-1" title={t('usuarios_mfa_restablecer')}
                        onClick={async () => { if (await dlg.confirmar(t('usuarios_mfa_confirmar', { nombre: u.username }), { peligro: true })) restablecerMfa.mutate(u.id); }}>
                        <ShieldOff className="w-4 h-4" />
                      </button>
                    )}
                    <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title={t('comun_eliminar')} onClick={async () => { if (await dlg.confirmar(t('usuarios_confirmar_eliminar', { nombre: u.username }), { peligro: true })) eliminar.mutate(u.id); }}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? t('usuarios_editar') : t('usuarios_nuevo')} onClose={() => setAbierto(false)}>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Campo label={t('usuarios_f_usuario')}><input className={fieldCls} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} /></Campo>
              <Campo label={t('usuarios_f_email')}><input type="email" className={fieldCls} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Campo>
              <Campo label={t('usuarios_f_empresa')}>
                <select className={fieldCls} value={form.empresa_id} disabled={!!editId} title={editId ? t('usuarios_empresa_fija') : undefined} onChange={(e) => setForm({ ...form, empresa_id: e.target.value })}>
                  <option value="">{t('comun_selecciona')}</option>{empresas.map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}
                </select>
              </Campo>
              <Campo label={t('usuarios_f_perfil')}>
                <select className={fieldCls} value={form.perfil_id} onChange={(e) => setForm({ ...form, perfil_id: e.target.value })}>
                  <option value="">{t('comun_selecciona')}</option>{perfiles.map((x) => <option key={x.id} value={x.id}>{x.nombre}</option>)}
                </select>
              </Campo>
            </div>
            <Campo label={editId ? t('usuarios_f_pass_opc') : t('usuarios_f_pass')}>
              <div className="relative">
                <KeyRound className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                <input type="password" className={`${fieldCls} pl-9`} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editId ? t('usuarios_pass_ph') : ''} />
              </div>
            </Campo>
            <Switch checked={form.activo} onChange={(v) => setForm({ ...form, activo: v })} label={t('comun_activo')} />
            <Campo label={t('usuarios_f_centros')}>
              <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-input p-2">
                {centrosCosto.length === 0 && <p className="text-xs text-muted-foreground">{t('usuarios_sin_centros')}</p>}
                {centrosCosto.map((c) => (
                  <label key={c.id} className="flex cursor-pointer items-center gap-2 text-sm">
                    <input type="checkbox" className="h-4 w-4 rounded border-input" checked={form.centros.includes(c.id)} onChange={() => toggleCentro(c.id)} />
                    {c.razon_social}
                  </label>
                ))}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{t('usuarios_n_sel', { n: form.centros.length })}</p>
            </Campo>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>{t('comun_cancelar')}</Button>
              <Button isLoading={guardar.isPending}
                disabled={!form.username.trim() || !form.email.trim() || !form.empresa_id || !form.perfil_id || (!editId && !form.password.trim())}
                onClick={() => guardar.mutate()}>{t('comun_guardar')}</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
